<template>
<select 
    v-model="selectedLang" 
    class="speaker-language-dropdown" 
    :disabled="disabled" 
    @change="$emit('change', $event.target.value)"
    @keydown="handleKeydown"
    @focus="resetFilter"
    ref="selectElement">
    <option value="" disabled selected>{{ placeholder }}</option>
    <option 
        v-for="lang in filteredLanguages" 
        :key="lang.code" 
        :value="lang.code"
        :style="{ display: lang.visible ? 'block' : 'none' }">
        {{ $t(lang.name) }}
    </option>
</select>
</template>

<script>
export default {
    name: 'SpeakerLanguageDropdown',
    props: {
        value: String,
        placeholder: String,
        languages: {
            type: Array,
            required: true
        },
        disabled: Boolean
    },
    data() {
        return {
            searchText: '',
            searchTimeout: null
        };
    },
    computed: {
        selectedLang: {
            get() {
                return this.value;
            },
            set(newValue) {
                this.$emit('input', newValue);
            }
        },
        sortedLanguages() {
            // Sort languages alphabetically by their translated names
            return [...this.languages].sort((a, b) => {
                const nameA = this.$t(a.name).toLowerCase();
                const nameB = this.$t(b.name).toLowerCase();
                return nameA.localeCompare(nameB);
            });
        },
        filteredLanguages() {
            if (!this.searchText) {
                return this.sortedLanguages.map(lang => ({ ...lang, visible: true }));
            }
            
            return this.sortedLanguages.map(lang => {
                const langName = this.$t(lang.name).toLowerCase();
                const isVisible = langName.includes(this.searchText.toLowerCase());
                return { ...lang, visible: isVisible };
            });
        }
    },
    methods: {
        handleKeydown(event) {
            // Only handle letter and number keys
            if (event.key.length === 1 && /[a-zA-Z0-9\s]/.test(event.key)) {
                event.preventDefault();
                
                // Clear previous timeout
                if (this.searchTimeout) {
                    clearTimeout(this.searchTimeout);
                }
                
                // Replace search text with the new typed character (fresh search each time)
                this.searchText = event.key;
                
                // Filter options and focus on first match
                this.filterAndFocusOptions();
                
                // Clear search text after 1 second of no typing
                this.searchTimeout = setTimeout(() => {
                    this.resetFilter();
                }, 1000);
            }
        },
        filterAndFocusOptions() {
            // Force re-render of options by updating the DOM
            this.$nextTick(() => {
                const select = this.$refs.selectElement;
                if (select) {
                    let firstVisibleIndex = -1;
                    
                    // Hide/show options based on filter and find first visible
                    const options = select.querySelectorAll('option');
                    options.forEach((option, index) => {
                        if (index === 0) return; // Skip placeholder
                        
                        const lang = this.filteredLanguages[index - 1];
                        if (lang) {
                            option.style.display = lang.visible ? 'block' : 'none';
                            
                            // Track first visible option
                            if (lang.visible && firstVisibleIndex === -1) {
                                firstVisibleIndex = index;
                            }
                        }
                    });
                    
                    // Focus on first visible option
                    if (firstVisibleIndex > 0) {
                        select.selectedIndex = firstVisibleIndex;
                        // Trigger change event to update the model
                        const firstVisibleLang = this.filteredLanguages.find(lang => lang.visible);
                        if (firstVisibleLang) {
                            this.$emit('input', firstVisibleLang.code);
                            this.$emit('change', firstVisibleLang.code);
                        }
                    }
                }
            });
        },
        filterOptions() {
            // Keep the old method for backward compatibility, but use the new one
            this.filterAndFocusOptions();
        },
        resetFilter() {
            this.searchText = '';
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
                this.searchTimeout = null;
            }
        }
    }
};
</script>
